import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Checkbox,
  ListItemText,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import axios from '../../Configs/axiosInstance';
import { MAIN_URL } from '../../Configs/Urls';
import dayjs from "dayjs";
import LogoImage from '../../Assets/images/Logos/logo.JPG';
import { colorForName, initialsFor } from '../PatientsPage/Components/avatarUtils';
import ContentPasteRoundedIcon from '@mui/icons-material/ContentPasteRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CircularProgress from '@mui/material/CircularProgress';

const categories = [
  "Early Morning",
  "Breakfast",
  "Mid Meal (after 2 hours)",
  "Lunch",
  "Evening",
  "Pre Dinner",
  "Dinner",
  'Post Dinner'
];

let setLocalStorageJSON = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};
let getLocalStorageJSON = (key) => {
  let dd = localStorage.getItem(key);
  return JSON.parse(dd);
};

// The backend returns a generic "Invalid Request" message plus a per-field
// `errors` object (e.g. { weight: ["The weight field is required."] }) on
// validation failure — surface the actual field message instead of just
// the generic one.
const getErrorMessage = (error) => {
  const data = error?.response?.data;
  const fieldErrors = data?.errors ? Object.values(data.errors).flat() : [];

  if (fieldErrors.length > 0) {
    return fieldErrors.join(" ");
  }

  return data?.message || "Error submitting form. Please try again.";
};

let removeLocalStorageJSON = (key) => {
  localStorage.removeItem(key);
};

const generateWeeklyRanges = (startDay = "Monday") => {
  const dayMap = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6
  };
  const weeks = [];
  const today = dayjs();
  const startOfWeek = today.startOf('week').add(dayMap[startDay], 'day');
  const oneYearFromToday = today.add(1, 'year');

  let currentStartOfWeek = startOfWeek;

  while (currentStartOfWeek.isBefore(oneYearFromToday)) {
    const startDate = currentStartOfWeek.format("DD-MM-YYYY");
    const endDate = currentStartOfWeek.add(6, 'day').format("DD-MM-YYYY");
    weeks.push({ date1: startDate, date2: endDate });
    currentStartOfWeek = currentStartOfWeek.add(7, 'day');
  }

  return weeks;
};



const categoryOrder = categories.reduce((acc, category, index) => {
  acc[category] = index;
  return acc;
}, {});

const getOrderedDays = (startDay) => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const startIndex = days.indexOf(startDay);
  return [...days.slice(startIndex), ...days.slice(0, startIndex)];
};


const PatientDietForm = ({ name, patientId, planId, selectedPlansWeeks, selectedWeekdefault, onCloseForm, showPatientInfo }) => {

  const [FetchedPatientData, setFetchedPatientData] = useState(null);
  const [FetchedDietPlan, setFetchedDietPlans] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState("Sunday");
  const [currentSelections, setCurrentSelections] = useState({});
  const [weight, setWeight] = useState("");
  const [selectedWeek, setSelectedWeek] = useState(selectedWeekdefault || "");
  const [weekStartDay, setWeekStartDay] = useState("Monday");

  useEffect(() => {

    if (planId == null) {

      let dataOfPreSave = getLocalStorageJSON(`patient_plan_${patientId}`);

      if (dataOfPreSave) {
        setWeekStartDay(dataOfPreSave.weekStartDay)
        setSelectedDay(dataOfPreSave.dayOfWeek)
        setCurrentSelections(dataOfPreSave.currentSelections)
        setSelectedWeek(dataOfPreSave.selectedWeek)
        setWeight(dataOfPreSave.weight)
      }

    }
  }, [])

  console.log("FetchedDietPlan is : ", FetchedDietPlan);

  const [mainDietPlans, setmainDietPlans] = useState({
    Sunday: {},
    Monday: {},
    Tuesday: {},
    Wednesday: {},
    Thursday: {},
    Friday: {},
    Saturday: {},
  });

  let pastePlan = () => {

    let dd = getLocalStorageJSON("CopiedPlan");

    setCurrentSelections(dd)
  }

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const weeklyRanges = generateWeeklyRanges(weekStartDay);

  const handleWeekChange = (event) => {

    let week = event.target.value;

    let dataOfPreSave = getLocalStorageJSON(`patient_plan_${patientId}`);

    if (dataOfPreSave) {

      let dd = {
        ...dataOfPreSave,
        selectedWeek: week
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd);

    } else {

      let dd = {
        dayOfWeek: "Sunday",
        currentSelections: {},
        selectedWeek: week
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd)

    }

    setSelectedWeek(week);
  };

  let fetchPatientData = async () => {
    await axios
      .get(`${MAIN_URL}patients/${patientId}`)
      .then((response) => {
        setFetchedPatientData(response.data);

      })
      .catch((error) => {
        setSnackbarMessage("Error fetching patient data.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };

  const fetchDietPlans = async () => {
    try {
      setLoading(true);
      // This dropdown needs the full diet item list, not one page of it —
      // /dietplans is paginated (10/page by default), so ask for a page
      // size well above the current item count.
      const response = await axios.get(`${MAIN_URL}dietplans`, { params: { per_page: 500 } });

      // Sort diet plans based on the category order
      const sortedDietPlans = response.data.data.sort((a, b) => {
        return categoryOrder[a.category] - categoryOrder[b.category];
      });

      setFetchedDietPlans(sortedDietPlans);
    } catch (error) {
      setSnackbarMessage("Failed to fetch DietPlans. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      fetchDietPlans();
      fetchPatientData();
    }
  }, [patientId]);

  let fetchPatientDietData = async () => {
    await axios
      .get(`${MAIN_URL}patientdietplans/${planId}`)
      .then((response) => {

        console.log("the response is ");

        setmainDietPlans((prev) => ({
          ...prev,
          ...response.data.DietPlan.DietPlan,
        }));
        setCurrentSelections((prev) => ({
          ...prev,
          ...response.data.DietPlan.DietPlan,
        }));

        let date = response.data.DietPlan.weekDateStart;    // "15-08-2025"

        // Convert to day name using dayjs
        const [day, month, year] = date.split('-');
        const formattedDay = dayjs(`${year}-${month}-${day}`).format('dddd'); // e.g., "Friday"

        setWeekStartDay(formattedDay)
        setSelectedDay(formattedDay)
        setWeight(response.data.DietPlan.weight)
      })
      .catch((error) => {
        setSnackbarMessage("Error fetching Diet Plan data.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };

  useEffect(() => {
    if (planId) {
      fetchPatientDietData();
    }
  }, [planId]);

  const handlePlanChange = (category, labels) => {

    const uniqueLabels = Array.from(
      new Set(labels)
    )

    let currentSelectionsCopy = { ...currentSelections };
    let newlyUpdated = {
      ...currentSelectionsCopy,
      [selectedDay]: {
        ...currentSelectionsCopy[selectedDay],
        [category]: uniqueLabels,
      },
    };

    let dataOfPreSave = getLocalStorageJSON(`patient_plan_${patientId}`);

    if (dataOfPreSave) {

      let dd = {
        ...dataOfPreSave,
        currentSelections: newlyUpdated
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd);

    } else {

      let dd = {
        dayOfWeek: "Sunday",
        currentSelections: newlyUpdated,
        selectedWeek: "",
        weekStartDay: "",
        weight: ""
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd)

    }

    setCurrentSelections(newlyUpdated);
  };

  const handleSave = () => {
    const allDaysEmpty = Object.values(currentSelections).every(dayPlan =>
      Object.values(dayPlan).every(categoryValues => categoryValues.length === 0)
    );

    if (selectedWeek == "") {
      setSnackbarMessage("Please Select the week for The Diet Plan");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setLoading(false);
      return;
    }

    if (!weight) {
      setSnackbarMessage("Please enter the patient's weight for this plan");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setLoading(false);
      return;
    }

    if (allDaysEmpty) {
      setSnackbarMessage("Select at least one Diet Rule for the Patient");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setLoading(false);
      return;
    }

    setmainDietPlans(currentSelections);

    let mainRequestStruc = {
      weight: weight,
      patientName: name,
      patientId,
      DietPlan: currentSelections,
      weekDateStart: selectedWeek.split(" - ")[0],
      weekDateEnd: selectedWeek.split(" - ")[1],

    };

    const method = planId ? "put" : "post";
    const url = planId
      ? `${MAIN_URL}patientdietplans/${planId}`
      : `${MAIN_URL}patientdietplans`;

    setLoading(true);

    axios[method](url, mainRequestStruc)
      .then((res) => {
        removeLocalStorageJSON(`patient_plan_${patientId}`)
        setSnackbarMessage(planId
          ? "Patient's Diet Plan updated successfully!"
          : "Patient's Diet Plan added successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);

        setTimeout(() => {
          onCloseForm();
        }, 2000);
        setLoading(false);
      })
      .catch((error) => {
        setSnackbarMessage(getErrorMessage(error));
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        setLoading(false);
      });
  };

  const handleDaySelect = (day) => {

    let dataOfPreSave = getLocalStorageJSON(`patient_plan_${patientId}`);

    if (dataOfPreSave) {

      let dd = {
        ...dataOfPreSave,
        dayOfWeek: day
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd)
    } else {

      let dd = {
        weight: "",
        currentSelections: {},
        dayOfWeek: day,
        selectedWeek: "",
        weekStartDay: ""
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd)

    }

    setSelectedDay(day);
  };


  const setWeekStartdayValue = (day) => {

    let dataOfPreSave = getLocalStorageJSON(`patient_plan_${patientId}`);

    if (dataOfPreSave) {

      let dd = {
        ...dataOfPreSave,
        weekStartDay: day
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd)
    } else {

      let dd = {
        weight: "",
        currentSelections: {},
        weekStartDay: day,
        dayOfWeek: "",
        selectedWeek: ""
      }

      setLocalStorageJSON(`patient_plan_${patientId}`, dd)

    }

    setWeekStartDay(day);
  };

  const [editingChip, setEditingChip] = useState(null);
  const [editValue, setEditValue] = useState("");

  console.log("FetchedDietPlan is : ", FetchedDietPlan);
  console.log("currentSelections is : ", currentSelections);
  console.log("selectedDay is : ", selectedDay);

  return (
    <Box>
      {
        showPatientInfo == true &&
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 2.5, borderRadius: 3, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, fontSize: '1.2rem', bgcolor: colorForName(FetchedPatientData?.name) }}>
              {initialsFor(FetchedPatientData?.name)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {FetchedPatientData?.name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                {FetchedPatientData?.gender && <Chip label={FetchedPatientData.gender} size="small" variant="outlined" />}
                <Typography variant="body2" color="text.secondary">{FetchedPatientData?.age} yrs</Typography>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography variant="body2" color="text.secondary">{FetchedPatientData?.contactNumber}</Typography>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography variant="body2" color="text.secondary">{FetchedPatientData?.email}</Typography>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      }

      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 2.5, borderRadius: 3, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
          {
            planId == null &&
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 2 }}>
              <FormControl variant="outlined" sx={{ width: 220 }}>
                <InputLabel id="week-start-label">Week Starts On</InputLabel>
                <Select
                  labelId="week-start-label"
                  value={weekStartDay}
                  onChange={(e) => { setWeekStartdayValue(e.target.value); handleWeekChange(e.target.value) }}
                  label="Week Starts On"
                >
                  {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Weight"
                variant="outlined"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                sx={{ width: 160 }}
                type="text"
              />

              <FormControl variant="outlined" sx={{ width: 260 }}>
                <InputLabel id="weekly-range-label">Select Week</InputLabel>
                <Select
                  labelId="weekly-range-label"
                  value={selectedWeek}
                  onChange={handleWeekChange}
                  label="Select Week"
                >
                  {weeklyRanges.map((week, index) => (
                    <MenuItem disabled={selectedPlansWeeks?.includes(`${week.date1} - ${week.date2}`)} key={index} value={`${week.date1} - ${week.date2}`}>
                      <Typography variant="body2" color="textPrimary">
                        {`${week.date1} - ${week.date2}`}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          }

          {
            planId != null &&
            <TextField
              label="Weight"
              variant="outlined"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              sx={{ width: 220 }}
              type="text"
            />
          }

          <Stack direction="row" spacing={2} alignItems="center">
            <img src={LogoImage} style={{ width: "56px", height: "56px", objectFit: 'contain' }} alt="" />
            <Button
              variant="outlined"
              startIcon={<ContentPasteRoundedIcon />}
              onClick={pastePlan}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Paste The Plan
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', overflow: 'hidden' }}>
        <Box display="flex">
          <Stack
            sx={{
              py: 2,
              px: 1,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            {getOrderedDays(weekStartDay).map((day) => (
              <Box
                key={day}
                onClick={() => handleDaySelect(day)}
                sx={{
                  width: "90px",
                  height: "34px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "3px 0",
                  borderRadius: "999px",
                  cursor: "pointer",
                  fontWeight: selectedDay === day ? 700 : 400,
                  bgcolor: selectedDay === day ? 'primary.main' : 'transparent',
                  color: selectedDay === day ? 'primary.contrastText' : 'text.primary',
                  '&:hover': { bgcolor: selectedDay === day ? 'primary.dark' : 'action.hover' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>{day}</Typography>
              </Box>
            ))}
          </Stack>

          <Box flexGrow={1} sx={{ p: { xs: 2, sm: 3 } }}>
            {categories?.map((category) => (
              <FormControl fullWidth variant="outlined" margin="normal" key={category}>
              <Autocomplete
                disableCloseOnSelect
                multiple
                options={Array.from(
                  new Set([
                    ...(FetchedDietPlan?.map((item) => item.value) || []),
                    ...(currentSelections[selectedDay]?.[category] || []),
                  ])
                )}
                value={currentSelections[selectedDay]?.[category] || []}
                onChange={(event, newValue) => handlePlanChange(category, newValue)}
                renderOption={(props, option, { selected }) => (
                  <MenuItem {...props} key={option} value={option}>
                    <Checkbox checked={selected} />
                    <ListItemText primary={option} />
                  </MenuItem>
                )}
                renderTags={(tagValues, getTagProps) =>
                  tagValues.map((option, index) => {
                    const isEditing = editingChip === option;
                    return isEditing ? (
                      <TextField
                        key={option}
                        size="small"
                        value={editValue}
                        autoFocus
                        onBlur={() => {
                          setEditingChip(null);
                          handlePlanChange(category, [
                            ...tagValues.filter((tag) => tag !== option),
                            editValue,
                          ]);
                        }}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setEditingChip(null);
                            handlePlanChange(category, [
                              ...tagValues.filter((tag) => tag !== option),
                              editValue,
                            ]);
                          }
                        }}
                      />
                    ) : (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        onDoubleClick={() => {
                          setEditingChip(option);
                          setEditValue(option);
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => <TextField {...params} label={category} placeholder="Search..." />}
              />
              </FormControl>
            ))}
          </Box>
        </Box>
      </Paper>

      <Button
        variant="contained"
        color="primary"
        disabled={loading}
        onClick={handleSave}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
        sx={{ mt: 3, borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600 }}
      >
        {planId ? "Update" : "Save"} Diet Plan
      </Button>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientDietForm;


